import { http } from '@/api/client/http';
import { MediaAssetListResponseSchema, MediaAssetResponseSchema } from '@/api/schemas/media';
import type { MediaAsset, MediaMimeType } from '@/api/schemas/media';

/**
 * Media module service — every API call this module makes passes through here.
 *
 * ---------------------------------------------------------------------------
 * THREE CALLS, AND THE MIDDLE ONE IS NOT THE API
 * ---------------------------------------------------------------------------
 *   1. `createAsset`  — `POST /content/media-assets`. Returns the asset and a
 *      presigned `uploadTarget`.
 *   2. `putBytes`     — a PUT to the object store, NOT to the API. The target
 *      URL is a bearer credential for a specific object key, so this call
 *      deliberately uses a plain `fetch` with no session cookie and no JSON
 *      envelope: the object store must not see the console's credentials, and
 *      the API must not proxy the bytes.
 *   3. `completeUpload` — `POST /content/media-assets/{id}/upload-completion`.
 *      The API then OBSERVES the object itself; it does not trust the bytes
 *      the caller claims to have sent.
 *
 * The `If-Match` header on step 3 carries the asset's version. M44's contract
 * reads it as a BARE integer (`1`, not `"1"`) — a different convention from
 * M13, and a quoted value is refused.
 */
export const MediaApi = {
  /** GET /content/media-assets — the tenant's media library. */
  listAssets: async (): Promise<MediaAsset[]> => {
    const response = await http.get('/content/media-assets', MediaAssetListResponseSchema);
    return response.data;
  },

  /** POST /content/media-assets — create the asset and get the upload target. */
  createAsset: async (input: {
    ownerId: string;
    mimeType: MediaMimeType;
    byteSize: number;
  }): Promise<MediaAsset> => {
    const response = await http.post('/content/media-assets', MediaAssetResponseSchema, {
      body: input,
      idempotencyKey: crypto.randomUUID(),
    });
    return response.data;
  },

  /**
   * PUT the bytes to the object store.
   *
   * NOT the JSON transport: the target is a different origin, the body is raw
   * bytes, and the request must not carry the console's session cookie.
   */
  putBytes: async (target: { url: string }, bytes: Blob, mimeType: string): Promise<void> => {
    const response = await fetch(target.url, {
      method: 'PUT',
      headers: { 'Content-Type': mimeType },
      body: bytes,
      credentials: 'omit',
    });

    if (!response.ok) {
      throw new Error(`The object store refused the upload (status ${response.status}).`);
    }
  },

  /** POST /content/media-assets/{id}/upload-completion — tell the API the bytes are there. */
  completeUpload: async (assetId: string, version: number): Promise<MediaAsset> => {
    const response = await http.post(
      `/content/media-assets/${assetId}/upload-completion`,
      MediaAssetResponseSchema,
      {
        body: {},
        ifMatch: version,
        // M44 reads the version as a BARE integer. The quoted ETag that M13
        // wants is refused here with a `400`, so the format is named rather
        // than left to the transport's default.
        ifMatchFormat: 'bare-version',
        idempotencyKey: crypto.randomUUID(),
      },
    );
    return response.data;
  },
};

/**
 * The whole upload, in order.
 *
 * Returns the SETTLED asset — the one the API observed, not the one it was
 * asked to accept. When the bytes do not arrive, the asset is left where it
 * stopped, and the caller is told why.
 */
export async function uploadAsset(input: {
  ownerId: string;
  mimeType: MediaMimeType;
  file: Blob;
}): Promise<MediaAsset> {
  const created = await MediaApi.createAsset({
    ownerId: input.ownerId,
    mimeType: input.mimeType,
    byteSize: input.file.size,
  });

  const target = created.uploadTarget;
  if (!target) {
    throw new Error('The API did not return an upload target for the new asset.');
  }

  await MediaApi.putBytes(target, input.file, input.mimeType);

  return MediaApi.completeUpload(created.mediaAssetId, created.version);
}
